import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import z from "zod";

import { NotConnected, useServer } from "@/components/server-hooks";
import { ServerPage } from "@/components/server-shell";
import {
  type Acct,
  type Health,
  OverviewTile,
  Rows,
  Section,
  SetupCard,
  type Sources,
  type Srv,
  Steps,
  type Tone,
} from "@/components/source-bits";
import { TelegramSource } from "@/components/telegram-source";
import { ago, Chip, errorMessage, Field, Select } from "@/components/ui-bits";
import { sealFor, useAgentAction, useAgentQuery } from "@/lib/act";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/servers/$id/sources")({
  validateSearch: z.object({ connected: z.string().optional(), error: z.string().optional() }),
  component: SourcesTab,
});

const PANEL = typeof window === "undefined" ? "" : window.location.origin;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Overview status for a list of accounts: failing beats reading beats paused. */
function overall(accts: (Health & { connected?: boolean; enabled?: boolean })[]): [string, Tone] {
  const live = accts.filter((a) => a.connected !== false);
  if (!live.length) return ["not connected", "off"];
  const failing = live.reduce((n, a) => n + (a.failures > 0 ? a.failures : 0), 0);
  if (failing) return [`${plural(failing, "failed run")}`, "warn"];
  if (!live.some((a) => a.enabled !== false)) return ["paused", "off"];
  const last = live.map((a) => a.last_ok).filter(Boolean).sort().pop();
  return [last ? `reading · ${ago(last)}` : "waiting for the first read", last ? "ok" : "info"];
}

function SourcesTabBody() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const server = useServer(id).data;
  const connected = server?.hub.connected;
  const status = useAgentQuery<Sources>(id, "sources_status", {}, !!connected);

  useEffect(() => {
    if (search.connected) toast.success(`Connected ${search.connected}`);
    if (search.error) toast.error(search.error);
  }, [search.connected, search.error]);

  if (!connected) return <NotConnected />;
  if (status.error) return <p className="text-sm text-[#F48A80]">{status.error.message}</p>;
  const st = status.data;
  if (!st || !server) return <p className="text-sm text-[#A8A59D]">Loading…</p>;
  const srv = server.server;
  const g = st.gmail.accounts;
  const tg = st.telegram;
  const gh = st.github;

  const tgStep = tg.login_step === "code" ? 3 : tg.login_step === "password" ? 4 : null;
  return (
    <div className="flex flex-col gap-7">
      <section aria-label="Overview" className="grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-3">
        <OverviewTile
          source="gmail"
          title="Gmail"
          detail={g.length ? `${plural(g.length, "inbox", "inboxes")}${g.some((a) => a.meet) ? " · Meet notes" : ""}` : "Your inboxes"}
          status={overall(g)[0]}
          tone={overall(g)[1]}
        />
        <OverviewTile
          source="slack"
          title="Slack"
          detail={st.slack.accounts.length ? plural(st.slack.accounts.length, "workspace") : "Your workspaces"}
          status={overall(st.slack.accounts)[0]}
          tone={overall(st.slack.accounts)[1]}
        />
        <OverviewTile
          source="notion"
          title="Notion"
          detail={
            st.notion.accounts.length
              ? `${plural(st.notion.accounts.length, "workspace")} · ${plural(st.notion.accounts.reduce((n, a) => n + a.databases, 0), "database")}`
              : "Task databases"
          }
          status={overall(st.notion.accounts)[0]}
          tone={overall(st.notion.accounts)[1]}
        />
        <OverviewTile
          source="github"
          title="GitHub"
          detail={gh.login ? `@${gh.login} · ${plural(gh.repos.length, "repo")}` : "Notifications and repos"}
          {...(() => {
            const [s, t] = gh.login
              ? overall([{ last_ok: gh.last_ok ?? null, failures: gh.failures ?? 0, last_error: gh.last_error ?? null, enabled: gh.enabled }])
              : (["not connected", "off"] as [string, Tone]);
            return { status: s, tone: t };
          })()}
        />
        <OverviewTile
          source="telegram"
          title="Telegram"
          detail={
            tg.logged_in
              ? tg.my_username
                ? `@${tg.my_username}`
                : "signed in"
              : tgStep
                ? `signing in · step ${tgStep} of 4`
                : tg.installed
                  ? "client installed"
                  : "Your chats"
          }
          status={
            tg.logged_in
              ? tg.enabled
                ? "reading"
                : "paused"
              : tgStep
                ? `waiting for your ${tgStep === 3 ? "code" : "password"}`
                : tg.login_step
                  ? "signing in"
                  : "not connected"
          }
          tone={tg.logged_in ? (tg.enabled ? "ok" : "off") : tg.login_step ? "info" : "off"}
          active={!tg.logged_in && !!tg.login_step}
        />
      </section>

      <Gmail id={id} srv={srv} st={st.gmail} all={st} />
      <Slack id={id} srv={srv} st={st.slack} all={st} />
      <Notion id={id} srv={srv} st={st.notion} all={st} />
      <GitHub id={id} srv={srv} st={st.github} />
      <TelegramSource id={id} srv={srv} st={st.telegram} history={tg.logged_in ? <HistoryRow id={id} source="telegram" st={st} /> : null} />
    </div>
  );
}

function Copy({ text }: { text: string }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <code className="rounded-[4px] bg-[#111111] px-1.5 py-0.5 font-mono text-[12px] break-all text-[#ECEAE4]">{text}</code>
      <Button size="xs" variant="outline" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Copied"))}>
        Copy
      </Button>
    </span>
  );
}

function healthText(h: Health) {
  return h.failures > 0 ? (
    <span className="text-[#F0B65A]" title={h.last_error ?? ""}>
      {plural(h.failures, "failed run")}: {h.last_error?.slice(0, 80)}
    </span>
  ) : (
    <>last read {ago(h.last_ok)}</>
  );
}

function AccountRow({ id, type, a, name, detail, extra }: { id: string; type: string; a: Acct; name: ReactNode; detail?: ReactNode; extra?: ReactNode }) {
  const toggle = useAgentAction<boolean>(id, "source_update", (enabled) => ({ type, account: a.account, enabled }), {
    refresh: ["sources_status"],
  });
  const remove = useAgentAction<void>(id, "source_remove", () => ({ type, account: a.account }), {
    refresh: ["sources_status"],
    success: "Removed",
  });
  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 px-5 py-3.5">
      <Chip value={!a.connected ? "error" : a.enabled ? "ok" : "neutral"} label={!a.connected ? "not signed in" : a.enabled ? "on" : "paused"} />
      <span className="min-w-0 flex-[1_1_260px] font-medium break-words">
        {name}{" "}
        <span className="font-normal text-[#A8A59D]">
          {detail}
          {a.connected && <> · {healthText(a)}</>}
        </span>
      </span>
      {extra}
      <Button size="sm" variant="ghost" disabled={toggle.isPending} onClick={() => toggle.mutate(!a.enabled)}>
        {a.enabled ? "Pause" : "Resume"}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="text-[#F48A80] hover:text-[#F48A80]"
        disabled={remove.isPending}
        onClick={() => window.confirm(`Remove ${a.account}? Its sign-in is deleted from your server.`) && remove.mutate()}
      >
        Remove
      </Button>
    </div>
  );
}

function HistoryRow({ id, source, st }: { id: string; source: string; st: Sources }) {
  const h = st.history?.[source];
  const start = useAgentAction<void>(id, "history_import", () => ({ source, days: st.history_days }), {
    refresh: ["sources_status"],
    success: `Importing ${st.history_days} days of ${source} history in the background`,
  });
  if (!h) return null;
  // e.g. "[gmail] yashgoyal: backfill 60d imported 376" for each account
  const last = h.last.length ? h.last.map((l) => l.replace(/^\S+\s+\[\w+\]\s*/, "")).join(" · ") : undefined;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2.5 px-5 py-3.5">
      <span className="min-w-0 flex-[1_1_320px] text-[13px] text-[#A8A59D]">
        History:{" "}
        {h.running
          ? "importing older messages now…"
          : h.queued.length
            ? `waiting for the first collection of ${h.queued.join(", ")}, then imports ${st.history_days} days`
            : (last ?? `older messages (up to ${st.history_days} days) aren't imported yet`)}{" "}
        · newly connected accounts triage the last {st.triage_days} days; older messages are history the agent can look up,
        never tasks.
      </span>
      <Button size="sm" variant="outline" disabled={h.running || start.isPending} onClick={() => start.mutate()}>
        Import {Math.round(st.history_days / 30)} months
      </Button>
    </div>
  );
}

function useOAuth(id: string) {
  return useMutation({
    mutationFn: (v: { provider: "google" | "slack"; app?: string; meet?: boolean }) =>
      client.servers.oauthStart({ id, provider: v.provider, app: v.app, meet: v.meet }),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
}

function Check({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex items-center gap-2 text-[13px] text-[#CFCBC1]">
      <input type="checkbox" className="size-4 accent-[#FF4F1A]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  );
}

/* ---------------- Gmail ---------------- */

function Gmail({ id, srv, st, all }: { id: string; srv: Srv; st: Sources["gmail"]; all: Sources }) {
  const redirect = `${PANEL}/oauth/google/callback`;
  const [meet, setMeet] = useState(false);
  const oauth = useOAuth(id);
  const setClient = useAgentAction<string>(id, "gmail_client_set", async (json) => ({ client: await sealFor(srv, "gmail-client", json) }), {
    refresh: ["sources_status"],
    success: "Google client saved on your server",
  });
  const c = st.client;
  const wrongType = c.present && (c.type !== "web" || !c.redirect_uris.includes(redirect));
  const setup = (
    <div className="flex flex-col gap-3">
      <Steps>
        <li>
          Open{" "}
          <a href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noreferrer">
            Google Cloud → New project
          </a>{" "}
          (any name, e.g. "fieldwork"), signed in with your Google account.
        </li>
        <li>
          In that project,{" "}
          <a href="https://console.cloud.google.com/apis/library/gmail.googleapis.com" target="_blank" rel="noreferrer">
            enable the Gmail API
          </a>{" "}
          (and the{" "}
          <a href="https://console.cloud.google.com/apis/library/drive.googleapis.com" target="_blank" rel="noreferrer">
            Drive API
          </a>{" "}
          if you want Google Meet notes).
        </li>
        <li>
          <a href="https://console.cloud.google.com/auth/overview" target="_blank" rel="noreferrer">
            Google Auth Platform
          </a>{" "}
          → Get started: an app name, your email, audience <b>External</b>. Then <b>Audience → Publish app</b>, so the sign-in
          doesn't expire after 7 days. Google will call it "unverified"; that's expected for an app only you use.
        </li>
        <li>
          Clients → Create client → type <b>Web application</b>, authorized redirect URI <Copy text={redirect} /> → Create →{" "}
          <b>Download JSON</b>.
        </li>
        <li>Choose that file here:</li>
      </Steps>
      <input
        type="file"
        accept="application/json,.json"
        className="text-[13px] text-[#CFCBC1] file:mr-3 file:h-9 file:rounded-[4px] file:border-0 file:bg-[#FF4F1A] file:px-3 file:font-mono file:text-[13px] file:text-[#111111]"
        disabled={setClient.isPending}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) setClient.mutate(await f.text());
          e.target.value = "";
        }}
      />
      {wrongType && (
        <p className="m-0 text-[13px] text-[#F0B65A]">
          This client is a "{c.type}" client{c.type === "web" ? " without the redirect URI above" : ""}. Connecting from the panel
          needs a <b>Web application</b> client with that redirect URI.
        </p>
      )}
    </div>
  );
  return (
    <Section
      id="gmail"
      title="Gmail"
      actions={
        c.present && (
          <>
            <Check checked={meet} onChange={setMeet}>
              also read Google Meet notes
            </Check>
            <Button disabled={oauth.isPending} onClick={() => oauth.mutate({ provider: "google", meet })}>
              Connect an inbox
            </Button>
          </>
        )
      }
    >
      {!c.present ? (
        <SetupCard active>
          <span className="font-medium">Step 1 (once): your own Google client</span>
          {setup}
        </SetupCard>
      ) : (
        st.accounts.length > 0 && (
          <Rows>
            {st.accounts.map((a) => (
              <AccountRow
                key={a.account}
                id={id}
                type="gmail"
                a={a}
                name={a.address}
                detail={a.meet ? "· Meet notes" : undefined}
                extra={
                  <Button size="sm" variant="outline" disabled={oauth.isPending} onClick={() => oauth.mutate({ provider: "google", meet: a.meet })}>
                    Reconnect
                  </Button>
                }
              />
            ))}
            {st.accounts.some((a) => a.connected) && <HistoryRow id={id} source="gmail" st={all} />}
          </Rows>
        )
      )}
      {c.present && (
        <details className="px-0.5 py-1" open={wrongType}>
          <summary className="cursor-pointer font-mono text-xs text-[#A8A59D]">
            Your Google client: {c.client_id.slice(0, 18)}… ({c.type === "web" ? "web application" : c.type}) · change
          </summary>
          <div className="mt-3">
            <SetupCard>{setup}</SetupCard>
          </div>
        </details>
      )}
      {c.present && st.accounts.length === 0 && (
        <p className="m-0 text-[13px] text-[#A8A59D]">Connect each inbox you want read. Google asks you to choose the account.</p>
      )}
    </Section>
  );
}

/* ---------------- Slack ---------------- */

const slackManifest = (redirect: string) => ({
  display_information: {
    name: "Fieldwork",
    description: "Read-only access so Fieldwork can keep its owner's task list in sync with Slack.",
  },
  oauth_config: {
    redirect_urls: [redirect],
    scopes: {
      user: ["channels:read", "channels:history", "groups:read", "groups:history", "im:read", "im:history", "mpim:read", "mpim:history", "users:read"],
    },
  },
  settings: { org_deploy_enabled: false, socket_mode_enabled: false, token_rotation_enabled: false },
});

function Slack({ id, srv, st, all }: { id: string; srv: Srv; st: Sources["slack"]; all: Sources }) {
  const redirect = `${PANEL}/oauth/slack/callback`;
  const manifest = JSON.stringify(slackManifest(redirect));
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [cid, setCid] = useState("");
  const [secret, setSecret] = useState("");
  const oauth = useOAuth(id);
  const save = useAgentAction<void, { app_file: string }>(
    id,
    "slack_app_set",
    async () => ({ name, client: await sealFor(srv, "slack-app", JSON.stringify({ client_id: cid.trim(), client_secret: secret.trim() })) }),
    { refresh: ["sources_status"], success: "Slack app saved; now connect it" },
  );
  const usedApps = new Set(st.accounts.map((a) => a.app_file));
  const pending = st.apps.filter((a) => !usedApps.has(a));
  const showSetup = adding || (st.accounts.length === 0 && pending.length === 0);
  return (
    <Section
      id="slack"
      title="Slack"
      actions={
        <Button variant={showSetup ? "ghost" : "outline"} onClick={() => setAdding(!adding)}>
          {adding ? "Close" : "Add a workspace"}
        </Button>
      }
    >
      {(st.accounts.length > 0 || pending.length > 0) && (
        <Rows>
          {st.accounts.map((a) => (
            <AccountRow key={a.account} id={id} type="slack" a={a} name={a.team} />
          ))}
          {pending.map((app) => (
            <div key={app} className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 px-5 py-3.5">
              <Chip value="warn" label="not connected" />
              <span className="flex-[1_1_260px] font-medium">
                {app.replace(/^slack-app-|\.json$/g, "")} <span className="font-normal text-[#A8A59D]">· app saved</span>
              </span>
              <Button size="sm" disabled={oauth.isPending} onClick={() => oauth.mutate({ provider: "slack", app })}>
                Connect
              </Button>
            </div>
          ))}
          {st.accounts.some((a) => a.connected) && <HistoryRow id={id} source="slack" st={all} />}
        </Rows>
      )}
      {showSetup && (
        <SetupCard active>
          <Steps>
            <li>
              <a href={`https://api.slack.com/apps?new_app=1&manifest_json=${encodeURIComponent(manifest)}`} target="_blank" rel="noreferrer">
                Create the Slack app from our manifest
              </a>
              , pick the workspace, and confirm. It only asks to <i>read</i> channels and messages you can see, as you.
            </li>
            <li>On the app's Basic Information page, copy the Client ID and Client Secret into the form below.</li>
            <li>Press Save and connect, and approve in Slack. Repeat for each workspace (each needs its own app).</li>
          </Steps>
          <form
            className="grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(undefined, {
                onSuccess: (r) => {
                  setCid("");
                  setSecret("");
                  setName("");
                  oauth.mutate({ provider: "slack", app: r.app_file });
                },
              });
            }}
          >
            <Field label="Short name">
              <Input className="h-11" placeholder="e.g. acme" value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))} required />
            </Field>
            <Field label="Client ID">
              <Input className="h-11 font-mono" value={cid} onChange={(e) => setCid(e.target.value)} required placeholder="1234.5678" />
            </Field>
            <Field label="Client Secret">
              <Input className="h-11 font-mono" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} required />
            </Field>
            <Button type="submit" size="lg" disabled={save.isPending || oauth.isPending}>
              Save and connect
            </Button>
          </form>
        </SetupCard>
      )}
    </Section>
  );
}

/* ---------------- Notion ---------------- */

type NotionDb = { id: string; title: string; people: string[]; statuses: string[]; url: string };
type Chosen = { id: string; people?: string[]; closed?: string[] };

function Notion({ id, srv, st, all }: { id: string; srv: Srv; st: Sources["notion"]; all: Sources }) {
  const [token, setToken] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const connect = useAgentAction<void, { account: string }>(
    id,
    "notion_connect",
    async () => ({ token: await sealFor(srv, "notion-token", token.trim()) }),
    { refresh: ["sources_status"], success: (r) => `Connected ${r.account}; now pick its task databases` },
  );
  const showSetup = adding || st.accounts.length === 0;
  return (
    <Section
      id="notion"
      title="Notion"
      actions={
        st.accounts.length > 0 && (
          <Button variant={adding ? "ghost" : "outline"} onClick={() => setAdding(!adding)}>
            {adding ? "Close" : "Add a workspace"}
          </Button>
        )
      }
    >
      {st.accounts.length > 0 && (
        <Rows>
          {st.accounts.map((a) => (
            <div key={a.account}>
              <AccountRow
                id={id}
                type="notion"
                a={a}
                name={a.workspace}
                detail={`· ${plural(a.databases, "task database")}`}
                extra={
                  <Button size="sm" variant="outline" aria-expanded={open === a.account} onClick={() => setOpen(open === a.account ? null : a.account)}>
                    {open === a.account ? "Close" : "Choose databases"}
                  </Button>
                }
              />
              {open === a.account && <NotionDatabases id={id} account={a.account} />}
            </div>
          ))}
          {st.accounts.some((a) => a.connected) && <HistoryRow id={id} source="notion" st={all} />}
        </Rows>
      )}
      {showSetup && (
        <SetupCard active>
          <Steps>
            <li>
              In Notion, in the workspace you want: Settings → <b>Developer tools → Personal access tokens</b> → create one. It sees
              what you can see; Fieldwork only ever reads.
            </li>
            <li>Paste it here, then choose which databases hold your tasks.</li>
          </Steps>
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              connect.mutate(undefined, {
                onSuccess: (r) => {
                  setToken("");
                  setAdding(false);
                  setOpen(r.account);
                },
              });
            }}
          >
            <Input
              className="h-11 flex-[1_1_320px] font-mono"
              type="password"
              autoComplete="off"
              placeholder="ntn_…"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
            />
            <Button type="submit" size="lg" disabled={connect.isPending}>
              {connect.isPending ? "Checking…" : "Connect"}
            </Button>
          </form>
        </SetupCard>
      )}
    </Section>
  );
}

function NotionDatabases({ id, account }: { id: string; account: string }) {
  const q = useAgentQuery<{ databases: NotionDb[]; chosen: Chosen[] }>(id, "notion_databases", { account });
  const [picked, setPicked] = useState<Record<string, Chosen>>({});
  useEffect(() => {
    if (q.data) setPicked(Object.fromEntries(q.data.chosen.map((c) => [c.id, c])));
  }, [q.data]);
  const save = useAgentAction<void>(id, "notion_set_databases", () => ({ account, databases: Object.values(picked) }), {
    refresh: ["sources_status", "notion_databases"],
    success: "Saved",
  });
  const grid = "grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.4fr)] items-center gap-2.5";
  if (q.error) return <p className="m-0 px-5 py-4 text-[13px] text-[#F48A80]">{q.error.message}</p>;
  if (!q.data) return <p className="m-0 px-5 py-4 text-[13px] text-[#A8A59D]">Loading databases…</p>;
  return (
    <div className="flex flex-col gap-2.5 border-t border-[#2A2927] px-5 py-4">
      <p className="m-0 text-[13px] text-[#A8A59D]">
        Tick the databases that hold tasks for you. "Assigned through" limits rows to ones where you're in that people field;
        "Closed when" lists the statuses that mean done.
      </p>
      {q.data.databases.length === 0 ? (
        <p className="m-0 text-[13px]">No databases visible to this token.</p>
      ) : (
        <div className={`${grid} font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]`}>
          <span>Database</span>
          <span>Rows</span>
          <span>Closed when</span>
        </div>
      )}
      {q.data.databases.map((d) => {
        const c = picked[d.id];
        return (
          <div key={d.id} className={`${grid} ${c ? "" : "text-[#A8A59D]"}`}>
            <label className="flex min-w-0 items-center gap-2">
              <input
                type="checkbox"
                className="size-4 shrink-0 accent-[#FF4F1A]"
                checked={!!c}
                onChange={(e) => {
                  const next = { ...picked };
                  if (e.target.checked) next[d.id] = { id: d.id, people: d.people.slice(0, 1), closed: d.statuses.includes("Done") ? ["Done"] : [] };
                  else delete next[d.id];
                  setPicked(next);
                }}
              />
              <a className="truncate" href={d.url} target="_blank" rel="noreferrer">
                {d.title}
              </a>
            </label>
            {c ? (
              <>
                <Select
                  aria-label={`Rows for ${d.title}`}
                  className="h-9 text-[13px]"
                  value={c.people?.[0] ?? ""}
                  onChange={(e) => setPicked({ ...picked, [d.id]: { ...c, people: e.target.value ? [e.target.value] : [] } })}
                >
                  <option value="">every row</option>
                  {d.people.map((p) => (
                    <option key={p} value={p}>
                      assigned through "{p}"
                    </option>
                  ))}
                </Select>
                <Input
                  aria-label={`Closed statuses for ${d.title}`}
                  className="h-9 text-[13px]"
                  placeholder="Done, Cancelled"
                  value={(c.closed ?? []).join(", ")}
                  onChange={(e) => setPicked({ ...picked, [d.id]: { ...c, closed: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })}
                  title={d.statuses.length ? `Statuses: ${d.statuses.join(", ")}` : undefined}
                />
              </>
            ) : (
              <>
                <span>—</span>
                <span>—</span>
              </>
            )}
          </div>
        );
      })}
      <Button className="mt-1 self-start" disabled={save.isPending} onClick={() => save.mutate()}>
        Save databases
      </Button>
    </div>
  );
}

/* ---------------- GitHub ---------------- */

function GitHub({ id, srv, st }: { id: string; srv: Srv; st: Sources["github"] }) {
  const [token, setToken] = useState("");
  const [showRepos, setShowRepos] = useState(false);
  const connect = useAgentAction<void, { login: string }>(
    id,
    "github_connect",
    async () => ({ token: await sealFor(srv, "github-token", token.trim()) }),
    { refresh: ["sources_status"], success: (r) => `Signed in to GitHub as ${r.login}; now pick repositories` },
  );
  const remove = useAgentAction<void>(id, "source_remove", () => ({ type: "github" }), { refresh: ["sources_status"], success: "GitHub disconnected" });
  const failing = (st.failures ?? 0) > 0;
  return (
    <Section id="github" title="GitHub">
      {st.configured && st.login ? (
        <Rows warn={failing}>
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 px-5 py-3.5">
            <Chip value={failing ? "warn" : st.enabled ? "ok" : "neutral"} label={failing ? plural(st.failures ?? 0, "failed run") : st.enabled ? "on" : "paused"} />
            <span className="min-w-0 flex-[1_1_300px] break-words">
              <span className="font-medium">@{st.login}</span>{" "}
              <span className="text-[#A8A59D]">
                · notifications and {plural(st.repos.length, "repository", "repositories")}
                {failing && st.last_error ? ` · last error: "${st.last_error.slice(0, 80)}"` : st.last_ok ? ` · last read ${ago(st.last_ok)}` : ""}
              </span>
            </span>
            <Button size="sm" variant="outline" aria-expanded={showRepos} onClick={() => setShowRepos(!showRepos)}>
              {showRepos ? "Close" : "Choose repositories"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-[#F48A80] hover:text-[#F48A80]"
              onClick={() => window.confirm("Disconnect GitHub?") && remove.mutate()}
            >
              Disconnect
            </Button>
          </div>
          {showRepos && <GitHubRepos id={id} />}
        </Rows>
      ) : (
        <SetupCard active>
          <Steps>
            <li>
              <a
                href="https://github.com/settings/tokens/new?scopes=repo,notifications,read:org&description=Fieldwork"
                target="_blank"
                rel="noreferrer"
              >
                Create a classic token
              </a>{" "}
              with the pre-ticked scopes (repo, notifications, read:org). Fine-grained tokens can't read notifications.
            </li>
            <li>Paste it here; your server signs the GitHub CLI in with it.</li>
          </Steps>
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              connect.mutate(undefined, {
                onSuccess: () => {
                  setToken("");
                  setShowRepos(true);
                },
              });
            }}
          >
            <Input
              className="h-11 flex-[1_1_320px] font-mono"
              type="password"
              autoComplete="off"
              placeholder="ghp_…"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
            />
            <Button type="submit" size="lg" disabled={connect.isPending}>
              {connect.isPending ? "Connecting…" : "Connect"}
            </Button>
          </form>
        </SetupCard>
      )}
    </Section>
  );
}

function GitHubRepos({ id }: { id: string }) {
  const q = useAgentQuery<{ repos: { name: string; pushed: string; private: boolean }[]; chosen: string[] }>(id, "github_repos");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (q.data) setPicked(new Set(q.data.chosen));
  }, [q.data]);
  const save = useAgentAction<void>(id, "github_set_repos", () => ({ repos: [...picked] }), {
    refresh: ["sources_status", "github_repos"],
    success: "Saved",
  });
  if (q.error) return <p className="m-0 px-5 py-4 text-[13px] text-[#F48A80]">{q.error.message}</p>;
  if (!q.data) return <p className="m-0 px-5 py-4 text-[13px] text-[#A8A59D]">Loading repositories…</p>;
  return (
    <div className="flex flex-col gap-2.5 px-5 py-4">
      <p className="m-0 text-[13px] text-[#A8A59D]">Notifications are always read. Also watch activity in these repositories (most recently pushed first):</p>
      <div className="grid max-h-72 grid-cols-[repeat(auto-fill,minmax(min(260px,100%),1fr))] gap-x-4 gap-y-1.5 overflow-auto">
        {q.data.repos.map((r) => (
          <label key={r.name} className="flex min-w-0 items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              className="size-4 shrink-0 accent-[#FF4F1A]"
              checked={picked.has(r.name)}
              onChange={(e) => {
                const n = new Set(picked);
                if (e.target.checked) n.add(r.name);
                else n.delete(r.name);
                setPicked(n);
              }}
            />
            <span className="truncate font-mono">{r.name}</span> <span className="shrink-0 text-[#A8A59D]">{ago(r.pushed)}</span>
          </label>
        ))}
      </div>
      <Button className="self-start" disabled={save.isPending} onClick={() => save.mutate()}>
        Save repositories
      </Button>
    </div>
  );
}

function SourcesTab() {
  return (
    <ServerPage
      title="Sources"
      subtitle={
        <span className="block max-w-[74ch]">
          Everything here is stored on your server only. Secrets you type are encrypted in this browser to your server's key
          before they're sent. New sources are picked up by the next 15-minute cycle.
        </span>
      }
    >
      <SourcesTabBody />
    </ServerPage>
  );
}
